import { z } from 'zod';

const ErrorPayloadSchema = z
  .object({
    error: z.union([z.string(), z.null(), z.undefined()]).optional(),
    code: z.union([z.string(), z.null(), z.undefined()]).optional(),
  })
  .passthrough();

const parseErrorPayload = (value: unknown): { message: string | null; code: string | null } => {
  const parsed = ErrorPayloadSchema.safeParse(value);
  if (!parsed.success) {
    return { message: null, code: null };
  }
  const messageValue = parsed.data.error;
  const codeValue = parsed.data.code;
  return {
    message: typeof messageValue === 'string' ? messageValue : null,
    code: typeof codeValue === 'string' ? codeValue : null,
  };
};

export async function parseJsonSafely(response: Response): Promise<{ message: string | null; code: string | null }> {
  let message: string | null = null;
  let code: string | null = null;

  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    try {
      const json: unknown = await response.clone().json();
      const parsed = parseErrorPayload(json);
      message = parsed.message ?? message;
      code = parsed.code ?? code;
    } catch {
      // Ignore JSON parse failure and fall back to reading the body as text.
    }
  }

  if (message === null) {
    try {
      const bodyText = await response.text();
      if (bodyText) {
        message = bodyText;
        if (!code && bodyText.trim().startsWith('{')) {
          try {
            const parsed: unknown = JSON.parse(bodyText);
            const parsedPayload = parseErrorPayload(parsed);
            if (parsedPayload.code) {
              code = parsedPayload.code;
            }
            if (parsedPayload.message) {
              message = parsedPayload.message;
            }
          } catch {
            // Keep the raw body text when parsing fails.
          }
        }
      }
    } catch {
      // Ignore body read errors and rely on fallback message.
    }
  }

  return { message, code };
}

export function formatSummarizeErrorMessage(code: string | null, rawMessage: string | null): string {
  const fallback = 'Unable to summarize link.';
  if (code === 'LINK_CONTENT_EMPTY') {
    return "We couldn't find enough readable text on that page to summarize. Try another link or add a transcript.";
  }
  if (code === 'LINK_CONTENT_FETCH_FAILED') {
    const trimmed = rawMessage?.trim();
    if (trimmed && trimmed.length > 0) {
      const normalized = trimmed.replaceAll(/\s+/g, ' ');
      return normalized.length > 500 ? `${normalized.slice(0, 497)}…` : normalized;
    }
    return "We couldn't load that page to summarize. Check the link and try again.";
  }
  if (code === 'LINK_SUMMARY_DISABLED') {
    const trimmed = rawMessage?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : 'Summaries are disabled for this link.';
  }

  if (rawMessage) {
    const trimmed = rawMessage.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        const parsedPayload = parseErrorPayload(parsed);
        if (parsedPayload.message) {
          return parsedPayload.message;
        }
      } catch {
        // No-op: fall back to trimmed message
      }
    }
    if (trimmed.length > 0) {
      return trimmed;
    }
  }

  return fallback;
}

export function isAbortError(value: unknown): boolean {
  if (value instanceof DOMException) {
    return value.name === 'AbortError';
  }
  if (value instanceof Error) {
    return value.name === 'AbortError';
  }
  return false;
}
